<template>
  <v-container>
    <v-card class="my-5" flat>
      <v-card-title>
        License Certificate
        <v-spacer />
        <!-- Commented out as iframe has built-in download functionality -->
        <!-- <v-btn
          small
          depressed
          color="secondary"
          v-if="document"
          @click="download"
        >
          <v-icon left>mdi-download</v-icon>
          Download
        </v-btn> -->

        <v-btn
          v-if="license && license.licenseBase64 !== ''"
          small
          depressed
          color="primary"
          class="ml-2"
          @click="
            $router.push({
              name: 'LicenseInvoice',
              params: { code: $route.params.code },
            })
          "
        >
          <span>
            <v-icon>mdi-cash</v-icon>
            Invoice
          </span>
        </v-btn>
      </v-card-title>

      <v-card-text>
        <v-row>
          <v-col cols="12" md="8">
            <!-- PDF Viewer Iframe (Desktop) / Mobile Placeholder -->
            <div
              v-if="document"
              class="pa-0"
              style="height: 80vh; min-height: 700px; position: relative; background: #525659"
              :style="{
                height: isMobile ? 'auto' : '80vh',
                minHeight: isMobile ? '300px' : '700px',
                position: 'relative',
                background: isMobile ? '#ffffff' : '#525659'
              }"
            >
              <!-- Loader while Blob URL is being created/loaded -->
              <div v-if="!pdfUrl" class="pa-4 white" style="height: 100%">
                <v-skeleton-loader type="image" height="100%" />
              </div>

              <template v-else>
                <!-- Mobile Optimized View -->
                <div v-if="isMobile" class="pa-12 text-center d-flex flex-column align-center justify-center">
                  <v-icon size="100" color="primary lighten-4">mdi-file-pdf-box</v-icon>
                  <div class="mt-6 grey--text text--darken-2 headline font-weight-bold">
                    Certificate Preview
                  </div>
                  <div class="mt-2 grey--text subtitle-1 mb-8">
                    Your certificate is ready. Since you are on a mobile device, please use the button below to view it.
                  </div>
                  <v-btn
                    x-large
                    color="primary"
                    class="text-none px-8 rounded-lg shadow-lg"
                    @click="openPdfInNewTab"
                  >
                    <v-icon left>mdi-eye</v-icon>
                    Open Certificate
                  </v-btn>
                  <div class="mt-4 grey--text caption">
                    The document will open in your device's native PDF viewer.
                  </div>
                </div>

                <!-- Desktop Iframe View -->
                <iframe
                  v-else
                  :src="pdfUrl"
                  width="100%"
                  height="100%"
                  frameborder="0"
                  style="border: none; display: block"
                  title="Certificate Viewer"
                  loading="lazy"
                ></iframe>
              </template>
            </div>
            <div v-else class="pa-4">
              <v-skeleton-loader type="image" height="700px" />
            </div>
          </v-col>
          <v-col
            cols="12"
            v-if="license && license.licenseBase64 === ''"
            md="4"
          >
            <h5>
              Download the application to sign/stamp then upload the scanned pdf
              document
            </h5>
            <div class="border">
              <div>
                <input
                  id="file"
                  ref="file"
                  type="file"
                  accept="application/pdf"
                  @change="handleFileUpload()"
                />
              </div>

              <span> {{ formData.file_name }}</span>

              <v-btn
                :disabled="formData.file === ''"
                block
                small
                class="mt-3"
                color="primary"
                @click="uploadFile"
              >
                Upload Application
                <v-icon>mdi-arrow-right</v-icon>
              </v-btn>
            </div>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
    <Checkout />
  </v-container>
</template>

<script>
import Checkout from "@kinetics254/paygate/src/views/Checkout.vue";
import { AuthService } from "../../auth";
import PdfViewerMixin from "../../../mixins/PdfViewerMixin";

export default {
  name: "LicenseDocument",
  components: { Checkout },
  mixins: [PdfViewerMixin],
  data: function () {
    return {
      formData: {
        file: "",
        file_name: "Attach Signed/Stamped File",
      },
    };
  },
  beforeRouteEnter(to, from, next) {
    next((v) => {
      v.$store.dispatch("License/getApplicationReport", v.$route.params.code);
      v.$store.dispatch("License/getLicence", v.$route.params.code);
    });
  },
  watch: {
    document: {
      handler(val) {
        if (val) {
          this.generatePdfUrl(val);
        }
      },
      immediate: true,
    },
  },
  computed: {
    document() {
      return this.$store.getters["License/licenseGetters"](
        "LicenseCertificate"
      );
    },

    license() {
      let service = this.$store.getters["License/licenseGetters"](
        "licenses"
      ).filter((s) => {
        return s.licenseApplicationNo === this.$route.params.code;
      });
      return service.pop();
    },
    user() {
      return AuthService.user;
    },
  },
  methods: {
    download: function () {
      let path = `data:application/pdf;base64,${this.document}`;
      Event.$emit("downloadfile", { code: `License_cerificate`, path });
    },

    pay: function () {
      let payload = {
        payerID: this.service.profileID,
        sourceDocumentNo: this.service.licenseApplicationNo,
        description: "License fees",
        amount: this.service.amount,
        accountNo: "",
        success_url: `${process.env.VUE_APP_URL}/${
          this.$router.resolve({ name: "LicenseList" }).href
        }`,
        fail_url: window.location.href,
      };

      payload = btoa(JSON.stringify(payload));
      // window.location.href = `${process.env.VUE_APP_PAYGATE_CHECKOUT_URL}/?payload=${payload}`;
      this.$checkout.pay(payload);
    },

    handleFileUpload: function () {
      let maxAllowedSize = 3 * 1024 * 1024;
      this.formData["file"] = this.$refs.file.files[0];

      if (this.formData["file"].size > maxAllowedSize) {
        Event.$emit("toasting", {
          message: "File too large. max allowed size is 2MB",
          type: "danger",
        });

        this.formData["file"] = "";
      }

      this.formData["file_name"] = this.$refs.file.files[0].name;
    },

    uploadFile: function () {
      let license = {
        licenseApplicationNo: this.$route.params.code,
        licenseBase64: this.formData.file,
      };

      this.$store.dispatch("License/updateLicence", license);
    },
  },
};
</script>

<style scoped>
.border {
  padding: 10px 20px;
  border: 1px solid #81a333;
}

iframe {
  border: none;
}

::-webkit-scrollbar {
  width: 8px;
}

::-webkit-scrollbar-track {
  background: #f1f1f1;
}

::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
  background: #555;
}

.shadow-lg {
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1),
    0 4px 6px -2px rgba(0, 0, 0, 0.05) !important;
}
</style>
